import React from "react";
import { useState, useEffect } from 'react';
import './classes/ManageProducts.css';
import { useParams } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import { Button } from '@mui/material';
import { configData } from "../config";
import { Helmet } from "react-helmet";
import Loading from './Loading';

const CourseMentorEdit = () => {

    const { productId } = useParams();
    const app_url = configData.APP_URL;

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [course, setCourse] = useState(null);
    const [rows, setRows] = useState([]);
    const [available, setAvailable] = useState([]);
    const [selectedMentor, setSelectedMentor] = useState('');
    const [message, setMessage] = useState('');

    const fetchMentors = async () => {
        try {
            setIsLoading(true);
            const response = await fetch(app_url + 'smwwcommerceproduct/getcoursementors', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ product_id: productId }),
            });
            const data = await response.json();
            if (data.data && data.data.msg === "Success") {
                setCourse(data.data.course);
                // Show positions as 1-based (1..N) based on the order returned.
                setRows(data.data.result.map((m, i) => ({
                    ...m,
                    sort_order: i + 1,
                })));
            }
        } catch (error) {
            console.error('Error fetching course mentors:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAvailable = async () => {
        try {
            const response = await fetch(app_url + 'smwwcommerceproduct/getavailablementors', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ product_id: productId }),
            });
            const data = await response.json();
            if (data.data && data.data.msg === "Success") {
                setAvailable(data.data.result);
            }
        } catch (error) {
            console.error('Error fetching available mentors:', error);
        }
    };

    useEffect(() => {
        fetchMentors();
        fetchAvailable();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId, app_url]);

    // Positions are always the row order, numbered 1..N.
    const renumber = (list) => list.map((row, i) => ({ ...row, sort_order: i + 1 }));

    // Move the mentor to the chosen position, shifting the others, so two
    // mentors can never share a number and no gaps are left.
    const handleSortOrderChange = (id, value) => {
        setRows((prev) => {
            const from = prev.findIndex((row) => row.id === id);
            if (from === -1) return prev;
            const to = Math.max(0, Math.min((parseInt(value, 10) || 1) - 1, prev.length - 1));
            const next = [...prev];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return renumber(next);
        });
    };

    const handleSaveOrder = async () => {
        try {
            setIsSaving(true);
            setMessage('');
            const response = await fetch(app_url + 'smwwcommerceproduct/updatementorsortorder', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    orders: rows.map((row) => ({
                        entity_id: row.id,
                        placement_page: row.placement_page,
                        sort_order: parseInt(row.sort_order, 10) || 0,
                    })),
                }),
            });
            const data = await response.json();
            if (data.data && data.data.msg === "Success") {
                setMessage('Sort order saved.');
                fetchMentors();
            } else {
                setMessage('Could not save sort order.');
            }
        } catch (error) {
            console.error('Error saving sort order:', error);
            setMessage('Could not save sort order.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddMentor = async () => {
        if (!selectedMentor) {
            return;
        }
        try {
            setMessage('');
            const response = await fetch(app_url + 'smwwcommerceproduct/addcoursementor', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    product_id: productId,
                    entity_id: selectedMentor,
                }),
            });
            const data = await response.json();
            if (data.data && data.data.msg === "Success") {
                setMessage('Mentor added.');
                setSelectedMentor('');
                fetchMentors();
                fetchAvailable();
            } else {
                setMessage((data.data && data.data.error) || 'Could not add mentor.');
            }
        } catch (error) {
            console.error('Error adding mentor:', error);
            setMessage('Could not add mentor.');
        }
    };

    const handleRemove = async (row) => {
        if (!window.confirm(`Remove ${row.name} from this course?`)) {
            return;
        }
        try {
            const response = await fetch(app_url + 'smwwcommerceproduct/removecoursementor', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    entity_id: row.id,
                    placement_page: row.placement_page,
                }),
            });
            const data = await response.json();
            if (data.data && data.data.msg === "Success") {
                setMessage(`${row.name} removed.`);
                setRows((prev) => renumber(prev.filter((r) => r.id !== row.id)));
                fetchAvailable();
            } else {
                setMessage('Could not remove mentor.');
            }
        } catch (error) {
            console.error('Error removing mentor:', error);
            setMessage('Could not remove mentor.');
        }
    };

    const columns = [
        {
            field: 'sort_order',
            headerName: 'Sort Order',
            headerClassName: 'product-sku-header',
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <select
                    value={params.row.sort_order}
                    onChange={(e) => handleSortOrderChange(params.row.id, e.target.value)}
                    style={{ padding: "4px" }}
                >
                    {rows.map((_, index) => (
                        <option key={index} value={index + 1}>{index + 1}</option>
                    ))}
                </select>
            ),
        },
        {
            field: 'name',
            headerName: 'Mentor Name',
            headerClassName: 'product-name-header',
            flex: 3,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <a
                    href={`/user/${params.row.id}/mentor`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#2C99D2" }}
                >
                    {params.row.name}
                </a>
            ),
        },
        {
            field: 'email',
            headerName: 'Email',
            headerClassName: 'product-sku-header',
            flex: 3,
            sortable: false,
            filterable: false,
        },
        {
            field: 'action',
            headerName: 'Action',
            headerClassName: 'super-app-theme--header',
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Button
                    variant="contained"
                    size="small"
                    color="error"
                    onClick={() => handleRemove(params.row)}
                    style={{ textTransform: "none" }}
                >
                    Remove
                </Button>
            ),
        },
    ];

    return (
        <>
            {isLoading ? (
                <Loading />
            ) : (
                <div id="main" className="manage-products-main">
                    <Helmet><title>Course Mentors</title></Helmet>
                    <div className="left"></div>
                    <div className="center left-center">
                        <h1>Mentors{course && course.product_name ? ' - ' + course.product_name : ''}</h1>
                        <div className="manage-products-container">
                            <div style={{ marginBottom: "10px" }}>
                                <a href="/admin/course-mentors">&larr; Back to Course Mentors</a>
                            </div>
                            {message && (
                                <div className="set-message-done">
                                    <span className="cart-message-icon message-icon"><i className="fa fa-check-circle"></i></span> {message}
                                </div>
                            )}
                            <div style={{ marginBottom: "15px", display: "flex", alignItems: "center", gap: "10px" }}>
                                <label htmlFor="add-mentor" style={{ fontWeight: "bold" }}>Add Mentor:</label>
                                <select
                                    id="add-mentor"
                                    value={selectedMentor}
                                    onChange={(e) => setSelectedMentor(e.target.value)}
                                    style={{ padding: "6px", minWidth: "260px" }}
                                >
                                    <option value="">- Select a mentor -</option>
                                    {available.map((m) => (
                                        <option key={m.id} value={m.id}>{m.name}</option>
                                    ))}
                                </select>
                                <Button
                                    variant="contained"
                                    onClick={handleAddMentor}
                                    disabled={!selectedMentor}
                                    style={{ backgroundColor: "#2C99D2", textTransform: "none" }}
                                >
                                    Add
                                </Button>
                            </div>
                            <div className="manage-products-table" style={{ width: '100%' }}>
                                <DataGrid
                                    autoHeight={true}
                                    rows={rows}
                                    columns={columns}
                                    getRowHeight={() => 'auto'}
                                    pageSizeOptions={[5, 10, 25, 50]}
                                    initialState={{
                                        pagination: { paginationModel: { page: 0, pageSize: 25 } },
                                    }}
                                    getRowId={(row) => row.id}
                                    disableRowSelectionOnClick
                                    localeText={{ noRowsLabel: 'No mentors assigned to this course.' }}
                                />
                            </div>
                            <div style={{ marginTop: "15px" }}>
                                <Button
                                    variant="contained"
                                    onClick={handleSaveOrder}
                                    disabled={isSaving || rows.length === 0}
                                    style={{ backgroundColor: "#2C99D2", textTransform: "none" }}
                                >
                                    {isSaving ? 'Saving...' : 'Save Sort Order'}
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className="right"></div>
                </div>
            )}
        </>
    );
};

export default CourseMentorEdit;
